import { unref } from 'vue';
import ApiClient from '@/ApiClient';
import { queryOptions, useQueryClient } from '@tanstack/vue-query';

export const actionRecognitionContentReport = () => {
  // No query-cache invalidation here: the reports list and pending-count on the
  // Recognition page are plain refs, refreshed by the parent's @actioned handler
  // (onReportActioned -> getContentReports/getContentReportsCount). The review
  // modal fetches per-entity details via getRecognitionContentReportsByEntity
  // with caching disabled, so there is no cached query keyed by entity to
  // invalidate.
  return {
    mutationFn: async ({ entityType, entityId, actionType }) => {
      return await ApiClient.actionRecognitionContentReport(entityType, entityId, actionType);
    },
  };
};

export const getRecognitionContentReports = (params = {}) => {
  return queryOptions({
    queryKey: ['recognition_content_reports', params],
    queryFn: async () => {
      return (await ApiClient.getRecognitionContentReports(unref(params))) || [];
    },
  });
};

export const getRecognitionContentReportsCount = () => {
  return queryOptions({
    queryKey: ['recognition_content_reports_count'],
    queryFn: async () => {
      return (await ApiClient.getRecognitionContentReportsCount()) || 0;
    },
  });
};

export const getRecognitionContentReportsByEntity = ({ entityType, entityId }) => {
  return queryOptions({
    // Per-entity key with caching disabled (gcTime/staleTime 0). The review
    // modal calls this imperatively via queryClient.fetchQuery on every open,
    // so each open re-issues a fresh request and a prior failed fetch never
    // lingers as a stale error on re-open.
    queryKey: ['recognition_content_reports_by_entity', entityType, entityId],
    queryFn: async () => {
      return (await ApiClient.getRecognitionContentReportsByEntity(entityType, entityId)) || [];
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
};

export const updateRecognitionReportSettings = () => {
  return {
    mutationFn: async ({ autoHide, maxReportsPerDay }) => {
      await ApiClient.updateRecognitionReportSettings({ autoHide, maxReportsPerDay });
      // Refresh global company state (State.setMe) so the saved settings are
      // reflected in content_moderation for the settings form.
      await ApiClient.getUser();
    },
  };
};

export const updateRecognitionBudgetSettings = () => {
  return {
    mutationFn: async ({ enforceManagerBudgets }) => {
      await ApiClient.updateRecognitionBudgetSettings({ enforceManagerBudgets });
      // Refresh global company state so recognition_properties.enforce_manager_budgets
      // is current for the settings form and ManagerBudgetsSection.
      await ApiClient.getUser();
    },
  };
};

export const getRecognitionLedger = () => {
  return queryOptions({
    queryKey: ['recognition_ledger'],
    queryFn: async () => {
      return await ApiClient.getRecognitionLedger();
    },
  });
};

export const getRecognitionInvoices = () => {
  return queryOptions({
    queryKey: ['recognition_invoices'],
    queryFn: async () => {
      const result = await ApiClient.getRecognitionInvoices();
      return result.data;
    },
  });
};

export const createRecognitionInvoice = () => {
  const queryClient = useQueryClient();

  return {
    mutationFn: async payload => {
      return await ApiClient.createRecognitionInvoice(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recognition_invoices'] });
      await queryClient.invalidateQueries({ queryKey: ['recognition_ledger'] });
    },
  };
};

export const deleteRecognitionInvoice = () => {
  const queryClient = useQueryClient();

  return {
    mutationFn: async invoiceId => {
      return await ApiClient.deleteRecognitionInvoice(invoiceId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recognition_invoices'] });
      await queryClient.invalidateQueries({ queryKey: ['recognition_ledger'] });
    },
  };
};

export const getRecognitionUnlinkedDeposits = (enabled = () => true) => {
  return queryOptions({
    enabled,
    queryKey: ['recognition_unlinked_deposits'],
    queryFn: async () => {
      const result = await ApiClient.getRecognitionUnlinkedDeposits();
      return result.data;
    },
  });
};

export const getRecognitionActivity = params => {
  return queryOptions({
    queryKey: ['recognition_activity', params],
    queryFn: async () => {
      const result = await ApiClient.getRecognitionActivity(unref(params));
      return result || [];
    },
  });
};

export const withdrawRecognitionGrant = () => {
  const queryClient = useQueryClient();

  return {
    mutationFn: async ({ grantId, reason }) => {
      return await ApiClient.withdrawRecognitionGrant(grantId, reason);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recognition_activity'] });
      await queryClient.invalidateQueries({ queryKey: ['recognition_ledger'] });
    },
  };
};

// No cache invalidation: both Upcoming tabs hold their awards in plain refs and refetch when the
// table reports a change. Nothing else moves either, because the award has not been granted yet.
export const withdrawUpcomingMilestoneAward = () => {
  return {
    mutationFn: async ({ award, reason }) => {
      return await ApiClient.withdrawUpcomingMilestoneAward(award, reason);
    },
  };
};

export const reinstateUpcomingMilestoneAward = () => {
  return {
    mutationFn: async award => {
      return await ApiClient.reinstateUpcomingMilestoneAward(award);
    },
  };
};

export const markRecognitionInvoiceComplete = () => {
  const queryClient = useQueryClient();

  return {
    mutationFn: async ({ invoiceId, depositIds }) => {
      return await ApiClient.markRecognitionInvoiceComplete(invoiceId, depositIds);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recognition_invoices'] });
      await queryClient.invalidateQueries({ queryKey: ['recognition_ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['recognition_unlinked_deposits'] });
    },
  };
};

export const getRecognitionBudgets = (params = {}) => {
  return queryOptions({
    queryKey: ['recognition_budgets', params],
    queryFn: async () => {
      return (await ApiClient.listRecognitionBudgets(unref(params))) || [];
    },
  });
};

export const createRecognitionBudget = () => {
  const queryClient = useQueryClient();

  return {
    mutationFn: async payload => {
      return await ApiClient.createRecognitionBudget(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recognition_budgets'] });
    },
  };
};

export const updateRecognitionBudget = () => {
  const queryClient = useQueryClient();

  return {
    mutationFn: async ({ budgetId, payload }) => {
      return await ApiClient.updateRecognitionBudget(budgetId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recognition_budgets'] });
    },
  };
};

export const createAwardBudget = () => {
  const queryClient = useQueryClient();

  return {
    mutationFn: async ({ programId, payload }) => {
      return await ApiClient.updateRecognitionProgram(programId, { budget: payload });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recognition_budgets'] });
    },
  };
};
